import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDate, toTitleCase, formatVerificationType } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES } from "@utils/indexedDb";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { REQUEST_LIST_SCAN_TYPE_OPTIONS, REQUEST_LIST_VERIF_TYPE_OPTIONS } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useVerificationTypeOptions } from "@hooks/useVerificationTypeOptions";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

const INITIAL_REQUEST_SEARCH_CRITERIA = {
  projectName: "",
  actionStatus: "",
  realFileName: "",
  createrId: "",
  assigneeId: "",
  completionRequestDt: "",
  verificationTypeCd: "",
};

const RequestList = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { hasSuperUserPermission, hasManagerPermission, hasProjectManagerPermission } = useRoutePermissions();
  const [requestList, setRequestList] = useState([]);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.REQUEST_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [searchCriteria, setSearchCriteria] = useState(INITIAL_REQUEST_SEARCH_CRITERIA);
  const [debouncedSearchCriteria, setDebouncedSearchCriteria] = useState(searchCriteria);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRequestListLoading, setIsRequestListLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const navigate = useNavigate();
  const isFirstRender = useRef(true);

  const { requestStatusList, creatorList, fetchSearchInfo, } = useSearchInfo();
  const { verificationTypeOptions } = useVerificationTypeOptions({});
  const translatedRequestStatusList = requestStatusList?.map((item) => {
    const translationMap = {
      "PENDING COMPLETION": "project.request.pending-completion",
      "PENDING APPROVAL": "project.request.pending-approval",
      DONE: "project.request.done",
    };

    return {
      ...item,
      label: t(translationMap[item.value] || item.label),
    };
  });

  const searchFields = [
    {
      name: "actionStatus",
      label: t("project.request.request-status"),
      type: "select",
      keepRawLabel: true,
      options: translatedRequestStatusList,
      placeholder: t("project.request.request-status"),
    },
    {
      name: "projectName",
      label: t("project.project-name"),
      placeholder: t("project.project-name"),
    },
    ...(hasSuperUserPermission || hasManagerPermission || hasProjectManagerPermission ? [
      {
        name: "assigneeId",
        label: t("communication.assignee"),
        type: "select",
        keepRawLabel: true,
        options: creatorList,
        displayFormatLabelWithID: true,
        placeholder: t("communication.assignee"),
      },
      {
        name: "createrId",
        label: t("project.requester-name"),
        type: "select",
        keepRawLabel: true,
        options: creatorList,
        displayFormatLabelWithID: true,
        placeholder: t("project.requester-name"),
      }
    ] : []),
    {
      name: "completionRequestDt",
      label: t("project.completion-request-date"),
      placeholder: t("project.completion-request-date"),
      type: "date",
    },
    {
      name: "verificationTypeCd",
      label: t("project.verification-type"),
      type: "select",
      options: verificationTypeOptions,
      placeholder: t("project.verification-type"),
    },
  ];

  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields,
    quickSearchField: "search",
    setSearchCriteria,
    initialCriteria: INITIAL_REQUEST_SEARCH_CRITERIA,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const columns = [
    "actionSeq",
    "actionStatus",
    "realFileName",
    "projectName",
    "createrName",
    "assigneeName",
    "completionRequestDt",
    "verificationTypeCd",
  ];

  const columnConfig = {
    actionSeq: {
      headerRender: () => t("project.request.request-id"),
      customRender: (value, row) => row.actionId || row.actionSeq,
    },
    actionStatus: {
      headerRender: () => t("project.request.request-status"),
      customRender: (value, row) => renderRequestStatusCell(row.actionStatus),
    },
    realFileName: {
      headerRender: () => t("project.request.verif-file-name"),
    },
    projectName: {
      headerRender: () => t("common.project-name"),
    },
    createrName: {
      headerRender: () => t("project.requester-name"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    assigneeName: {
      headerRender: () => t("communication.assignee"),
    },
    completionRequestDt: {
      headerRender: () => t("project.completion-request-date"),
      customRender: (value, row) => {
        return <div>{formatDate(row.completionRequestDt) || "-"}</div>;
      },
    },
    verificationTypeCd: {
      headerRender: () => t("project.verification-type"),
      customRender: (value, row) => {
        return <div>{formatVerificationType(row.verificationTypeCd)}</div>;
      },
    },
  };

  const renderRequestStatusCell = (status) => {
    if (!status) {
      return <span>-</span>;
    }

    const statusStyles = {
      "PENDING COMPLETION": colors.requestStatusPendingCompletion,
      "PENDING APPROVAL": colors.requestStatusPendingApproval,
      DONE: colors.requestStatusDone,
    };

    // Map status to translation keys
    const statusTranslations = {
      "PENDING COMPLETION": t("project.request.pending-completion"),
      "PENDING APPROVAL": t("project.request.pending-approval"),
      DONE: t("project.request.done"),
    };

    return (
      <span
        className={`inline-flex items-center rounded-2xl px-2 py-1 text-sm leading-[16px] font-semibold ${statusStyles[status] || colors.badgeVariantDefault}`}
      >
        {statusTranslations[status] || toTitleCase(status)}
      </span>
    );
  };

  // Controls
  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleCheckboxChange = (newSelectedRows) => setSelectedRows([...newSelectedRows]);
  const onRowClick = (rowData) => {
    const verificationType = rowData.verificationTypeCd || 'clarity'; // Default to clarity if not available
    navigate(`./${rowData.actionSeq}/${verificationType.toLowerCase()}`);
  };

  // Search handler
  const searchHandler = useCallback((inputData) => {
    setSearchCriteria((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
  };

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Show loading when user is typing (only when there's actual change)
    const hasActualChange = Object.values(searchCriteria).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchCriteria(searchCriteria);
      setIsSearching(false);
    }, 0);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchCriteria]);

  // Call API when debouncedSearchCriteria or sorting changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchData();
    }
  }, [debouncedSearchCriteria, sortColumn, sortDirection]);

  // Call API when currentPage changes (but not when it's reset to 1 due to search)
  useEffect(() => {
    // Only fetch if this is not the first render and currentPage is not being reset due to search
    if (!isFirstRender.current && debouncedSearchCriteria === searchCriteria) {
      fetchData();
    }
    isFirstRender.current = false;
  }, [currentPage, itemsPerPage]);

  // --- RESTORED FETCH LOGIC ---
  useEffect(() => {
    fetchSearchInfo && fetchSearchInfo();
  }, []);

  const getListData = async (bodyRequest = {}) => {
    setIsRequestListLoading(true);
    try {
      const {
        pageSize = itemsPerPage,
        startRow = 0,
        sortKey = sortColumn || "created_At",
        sortOrder = sortDirection || "desc",
        ...filters
      } = bodyRequest;
      const response = await getIndexDBData(STORES.REQUESTS, {
        pageSize,
        startRow,
        filters,
        sortKey,
        sortOrder,
      });

      if (response && typeof response !== "string") {
        setRequestList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching requests from IndexedDB:", error);
    } finally {
      setIsRequestListLoading(false);
    }
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(debouncedSearchCriteria, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }

      if (value !== "" && value !== undefined && value !== null) {
        // Format date for API if needed
        if (
          (key === "completionRequestDt") &&
          value instanceof Date
        ) {
          acc[key] = formatDate(value); // Format: YYYY-MM-DD
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    bodyRequest["sortKey"] = sortColumn || "createdAt";
    bodyRequest["sortOrder"] = sortDirection || "desc";
    getListData(bodyRequest);
  };

  // Cleanup effect to clear debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="request-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="request-list-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid="request-list-title">{t("project.request.request-list")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`} data-testid="request-list-controls">
        <TableHeaderControls
          controlsTestId="request-list-table-header-controls"
          searchInputTestId="request-list-search-input"
          advancedSearchButtonTestId="request-list-advanced-search-button"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          searchField="search"
          searchValue={searchCriteria.search ?? ""}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          placeholderSearch={t("common.search-placeholder")}
          isDisplayAddButton={false}
          isDisplayDeleteButton={false}
          isSearching={isSearching}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="request-list-advanced-search">
          <AdvancedSearch
            data-testid="request-list-advanced-search-panel"
            title={t("common.advanced-search")}
            searchFields={searchFields}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Table */}
      <div className="flex-grow min-h-0" data-testid="request-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={requestList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isRequestListLoading || isSearching}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={false}
          hasRadioButton={false}
          rowKey="actionSeq"
          shouldShowGoToPage={true}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showItemsPerPageSelector={true}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortable={true}
        />
      </div>
    </div>
  );
};

export default RequestList;
